import {
  defaultFilterProviderOptions,
  type SuppliedFilterProviderOptions,
  type ValueRowProps,
  type FilterProvider,
  type AnyBlock,
  type MutableFilterBlock,
  type ARIAFilterSuggestion,
  type FilterSuggestion,
  FilterValueType,
  type FilterValueData,
  type FilterConfig,
  type IndexedBlockValueItem,
} from '../types'
import {RootFilterProvider} from './root'
import {FILTER_KEYS} from '../constants/filter-constants'
import {getFilterValue, getLastFilterBlockValue} from '../utils'
import {hasMatch, score} from 'fzy.js'
import {getValueIcon} from './ValueIcon'

export class NoFilterProvider extends RootFilterProvider implements FilterProvider {
  filterValues: ARIAFilterSuggestion[]

  constructor(filterValues: FilterSuggestion[], options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.no, options)

    this.options = {
      ...defaultFilterProviderOptions,
      ...options,
      filterTypes: {...defaultFilterProviderOptions.filterTypes, valueless: false, ...options?.filterTypes},
    }

    this.filterValues = filterValues.map(fv => ({...fv, ariaLabel: `${fv.displayName}, ${this.displayName}`}))
  }

  async getSuggestions(filterBlock: AnyBlock | MutableFilterBlock, config: FilterConfig, caretIndex?: number | null) {
    if (!this.shouldGetSuggestions(filterBlock)) return null

    const lastValue = getLastFilterBlockValue(filterBlock, caretIndex)
    const suggestions: ARIAFilterSuggestion[] = []

    if (!lastValue) return this.filterValues

    for (const filterValue of this.filterValues) {
      const {value: fValue, displayName} = filterValue
      const value = getFilterValue(fValue)

      const matchesValue = !!value && hasMatch(lastValue, value)
      const matchesDisplayName = !!displayName && hasMatch(lastValue, displayName)

      if (!value || (!matchesValue && !matchesDisplayName)) continue
      filterValue.priority -= score(lastValue, value)

      suggestions.push({...filterValue, type: FilterValueType.Value})
    }

    return suggestions
  }

  async validateFilterBlockValues(values: IndexedBlockValueItem[]): Promise<IndexedBlockValueItem[]> {
    return values.map(v => {
      return {
        ...v,
        valid: !!this.filterValues.find(fv => fv.value === v.value),
      }
    })
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      leadingVisual: getValueIcon({value}),
    }
  }
}
